/**
 * Abstraction storage selon plateforme.
 *
 * - Web : cookie httpOnly géré par l'API (rien à stocker côté JS pour le refresh).
 *   On garde uniquement des préférences UX non sensibles via Preferences (= localStorage natif).
 * - Mobile (iOS/Android) : refresh token en stockage sécurisé natif
 *   (iOS Keychain / Android EncryptedSharedPreferences) via SQLite-stockage chiffré.
 *
 * Aucun token sensible (refresh JWT, clé MFA, payload vault) ne doit transiter par
 * localStorage / sessionStorage / IndexedDB côté Web — un XSS exfiltre tout.
 * Sur Web, le refresh token reste côté serveur (cookie __Host- httpOnly Secure SameSite=Strict).
 */
import { Preferences } from '@capacitor/preferences';
import { CapgoCapacitorDataStorageSqlite } from '@capgo/capacitor-data-storage-sqlite';
import { isNative } from './platform';

const SECURE_STORE = 'jx_secure';
let secureInitDone = false;

async function ensureSecureStoreReady(): Promise<void> {
  if (secureInitDone || !isNative()) return;
  await CapgoCapacitorDataStorageSqlite.openStore({
    database: SECURE_STORE,
    table: 'kv',
    encrypted: true,
    mode: 'secret',
  });
  secureInitDone = true;
}

/**
 * Stockage SENSIBLE (tokens, refresh JWT, secrets).
 * Native uniquement — sur Web, refuse l'écriture (utiliser le cookie httpOnly côté serveur).
 */
export const secureStorage = {
  async set(key: string, value: string): Promise<void> {
    if (!isNative()) {
      throw new Error('secureStorage.set() is mobile-only. Use the httpOnly cookie on web.');
    }
    await ensureSecureStoreReady();
    await CapgoCapacitorDataStorageSqlite.set({ key, value });
  },

  async get(key: string): Promise<string | null> {
    if (!isNative()) return null;
    await ensureSecureStoreReady();
    const { value } = await CapgoCapacitorDataStorageSqlite.get({ key });
    return value ?? null;
  },

  async remove(key: string): Promise<void> {
    if (!isNative()) return;
    await ensureSecureStoreReady();
    await CapgoCapacitorDataStorageSqlite.remove({ key });
  },

  async clear(): Promise<void> {
    if (!isNative()) return;
    await ensureSecureStoreReady();
    await CapgoCapacitorDataStorageSqlite.clear();
  },
};

/**
 * Préférences UX NON sensibles (thème, dernière vue, langue, etc.).
 * OK sur Web et Mobile.
 */
export const prefs = {
  async set(key: string, value: string): Promise<void> {
    await Preferences.set({ key, value });
  },
  async get(key: string): Promise<string | null> {
    const { value } = await Preferences.get({ key });
    return value;
  },
  async remove(key: string): Promise<void> {
    await Preferences.remove({ key });
  },
};
